import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { memo } from 'react';

import { Box, Button, Modal, Typography } from '@core/component';
import { Translate } from '@core/constant';
import { useContextCurrency } from '@core/context';
import { useLanguage } from '@core/hook';
import {
  EActionAttribute,
  EButtonVariant,
  EFontWeight,
  ESize,
  ETypographyVariant,
} from '@core/type';
import { formatCurrency } from '@core/util';

import { ModalPricingDetailsProps, TextDescriptionProps } from '../interface-modal';

const TextDescription = ({ tKey, isTitleUppercase = true }: TextDescriptionProps) => (
  <Typography>
    <Trans
      i18nKey={`${Translate.common.PRODUCT}:${tKey}`}
      components={{
        boldText: (
          <Typography
            as={EActionAttribute.SPAN}
            fontWeight={EFontWeight.BOLD}
            textTransform={isTitleUppercase ? 'uppercase' : undefined}
          />
        ),
      }}
    />
  </Typography>
);

const ModalPricingDetailsContainer = ({
  isOpen,
  onCloseModal,
  totalPrice,
  metalPrice,
  vatPrice,
  premiumPrice,
  isDeliveryPrice,
  deal,
  currencyIso,
  totalPriceWithDeal,
}: ModalPricingDetailsProps) => {
  const { currency: currencySSR } = useContextCurrency();
  const { language } = useLanguage();
  const { t: tProduct } = useTranslation(Translate.common.PRODUCT);

  const currency = currencyIso || currencySSR;
  const dealAmount = deal?.amount || 0;

  return (
    <Modal isOpen={isOpen} onCloseModal={onCloseModal} size={ESize.LG}>
      <Modal.Header>
        <Typography textAlign="center" variant={ETypographyVariant.H4}>
          {tProduct('modals.pricingDetails.title')}
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <Box display="flex" gap="32px" marginBottom="32px">
          <Box display="flex" flexDirection="column" gap="8px">
            <Typography variant={ETypographyVariant.H6}>
              {tProduct('modals.pricingDetails.totalPrice')}
            </Typography>
            <Typography>{tProduct('modals.pricingDetails.metalValue')}</Typography>
            <Typography>{tProduct('modals.pricingDetails.fees')}</Typography>
            <Typography>{tProduct('modals.pricingDetails.vat')}</Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap="8px">
            <Typography fontWeight={EFontWeight.BOLD}>
              {formatCurrency(language, currency, deal ? totalPriceWithDeal : totalPrice)}
            </Typography>
            <Typography fontWeight={EFontWeight.BOLD}>
              {formatCurrency(language, currency, metalPrice)}
            </Typography>
            <Typography fontWeight={EFontWeight.BOLD}>
              {formatCurrency(language, currency, premiumPrice - dealAmount)}
            </Typography>
            <Typography fontWeight={EFontWeight.BOLD}>
              {formatCurrency(language, currency, vatPrice)}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap="16px">
          {isDeliveryPrice && (
            <TextDescription
              isTitleUppercase={false}
              tKey={'modals.pricingDetails.deliveryNoteText'}
            />
          )}
          <TextDescription tKey={'modals.pricingDetails.totalPriceDescription'} />
          <TextDescription tKey={'modals.pricingDetails.metalValueDescription'} />
          <TextDescription tKey={'modals.pricingDetails.feesDescription'} />
          <TextDescription
            tKey={`modals.pricingDetails.${
              isDeliveryPrice ? 'deliveryVatDescription' : 'storeVatDescription'
            }`}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box textAlign="center">
          <Button variant={EButtonVariant.OUTLINED} onClick={onCloseModal}>
            {tProduct(`${Translate.common.BUTTON}:close.title`)}
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
};

export const ModalPricingDetails = memo(ModalPricingDetailsContainer);
